import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import {
  LogoAzure,
  LogoBitbucket,
  LogoGithub,
  LogoGitlab,
  LogoRawGit,
  LogoShowcase,
  LogoSpaceliftColored,
} from "components/icons/generated";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import { IconComponent } from "types/Icon";
import { VcsProvider } from "types/generated";

import { getManageIntegrationURL } from "../../VcsShared/helpers";
import useGetDefaultVcsIntegration from "../useGetDefaultVcsIntegration";

const providerDetails: Record<VcsProvider, { name: string; icon: IconComponent }> = {
  [VcsProvider.AzureDevops]: { name: "Azure DevOps", icon: LogoAzure },
  [VcsProvider.BitbucketCloud]: { name: "Bitbucket Cloud", icon: LogoBitbucket },
  [VcsProvider.BitbucketDatacenter]: { name: "Bitbucket Data Center", icon: LogoBitbucket },
  [VcsProvider.GithubEnterprise]: { name: "GitHub", icon: LogoGithub },
  [VcsProvider.Gitlab]: { name: "GitLab", icon: LogoGitlab },
  [VcsProvider.Github]: { name: "GitHub", icon: LogoGithub },
  [VcsProvider.Showcase]: { name: "Showcase", icon: LogoShowcase },
  [VcsProvider.Git]: { name: "Git", icon: LogoRawGit },
  // TODO: add Spacelift integration icon when available
  [VcsProvider.Spacelift]: { name: "Spacelift", icon: LogoSpaceliftColored },
};

type SetupButtonProps = {
  provider: VcsProvider;
};

const SetupButton = ({ provider }: SetupButtonProps) => {
  const trackEvent = useAnalytics();
  const navigate = useNavigate();
  const { loading, isSetupDisabled } = useGetDefaultVcsIntegration();

  const chooseProviderClickHandler = useCallback(
    (provider: VcsProvider) => () => {
      trackEvent("Setup Integration Select", { integration: provider });
      navigate(getManageIntegrationURL(provider));
    },
    [trackEvent, navigate]
  );

  const isDisabled = isSetupDisabled(provider);

  return (
    <Tooltip
      active={isDisabled}
      widthMode="maxWidthSm"
      on={(tooltipProps) => (
        <Button
          variant="primary"
          loading={loading}
          disabled={isDisabled}
          onPress={chooseProviderClickHandler(provider)}
          startIcon={providerDetails[provider].icon}
          {...tooltipProps}
        >
          Set up {providerDetails[provider].name}
        </Button>
      )}
    >
      <>
        The ability to make multiple concurrent VCS integrations of the same type is an Enterprise
        feature.
        <br />
        <br />
        Please remove one of the integrations or upgrade your plan to create multiple concurrent VCS
        connections.
      </>
    </Tooltip>
  );
};

export default SetupButton;
